/*eslint-disable*/
import React, { useEffect, useState } from 'react';
import { Col, Form, Input, Row, Select } from 'antd';
import InputLabel from 'components/InputLabel/index';
import { NumericFormat } from 'react-number-format';
import { withValueCap } from 'pages/globals/global';
import TableContentModal from '../LeaseUtils/Modal/TableContentModal';
import CardAccordion from '../CardAccordion';
import GrossRent from './GrossRent';
import AdvanceAmount from './AdvanceAmount';
import { constantFormValues } from '../../root';
/**
 * FinancialTermsConditions component renders the financial terms and conditions section
 * with form fields for the lease record management.
 *
 * This component should be rendered within a Form component context since it uses
 * Form.Item components that depend on the form context.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.screen - Current screen mode ('CREATE', 'EDIT', or 'VIEW')
 * @param {Object} props.constantFormValues - Form configuration values
 * @param {function} props.handleLeaseDateChange - Function to handle lease date changes
 * @param {Object} props.form - Form instance (needed for endOfFinancialYear field)
 * @param {Object} props.LeaseEndDate - Lease end date value
 *
 * @returns {JSX.Element} The Financial Terms & Conditions form section
 */
/*eslint-disable*/
const FinancialTermsConditions = ({ screen, form, statusData }) => {
	const [showGrossRentModal, setShowGrossRentModal] = useState(false);
	const [showAdvanceAmountModal, setAdvanceAmountModal] = useState(false);

	const grossRentList = Form.useWatch('grossRentList', form);
	const advanceAmountList = Form.useWatch('advanceAmountList', form);

	// useEffect(() => {
	// 	if (grossRentList && advanceAmountList) {
	// 		const maxGrossRentTerm = Math.max(...grossRentList.map((item) => item.to));
	// 		const maxAdvanceAmountTerm = Math.max(...advanceAmountList.map((item) => item.to));
	// 		form.setFieldValue("leaseTerm", Math.max(maxGrossRentTerm, maxAdvanceAmountTerm));
	// 		if (form.getFieldValue('leaseStartDate') && form.getFieldValue('leaseTerm')) {
	// 			const leaseStartDate = form.getFieldValue('leaseStartDate');
	// 			const leaseTerm = form.getFieldValue('leaseTerm');
	// 			const leaseEndDate = leaseStartDate.add(leaseTerm, 'month').subtract(1, 'day');
	// 			form.setFieldValue('leaseEndDate', leaseEndDate); // Updates leaseEndDate automatically
	// 		}
	// 	}
	// }, [grossRentList, advanceAmountList]);

	useEffect(() => {
		if (!Array.isArray(grossRentList) || !Array.isArray(advanceAmountList)) return;
		if (grossRentList.length === 0 && advanceAmountList.length === 0) return;

		const maxGrossRentTerm = Math.max(...grossRentList.map((item) => item.to ?? 0));
		const maxAdvanceAmountTerm = Math.max(...advanceAmountList.map((item) => item.to ?? 0));
		const maxTerm = Math.max(maxGrossRentTerm, maxAdvanceAmountTerm);

		form.setFieldValue('leaseTerm', maxTerm);

		const leaseStartDate = form.getFieldValue('leaseStartDate');

		if (leaseStartDate && maxTerm) {
			const leaseEndDate = leaseStartDate.add(maxTerm, 'month').subtract(1, 'day');
			form.setFieldValue('leaseEndDate', leaseEndDate);
		}
	}, [grossRentList, advanceAmountList]);

	const grossRentModalData = {
		head: 'Monthly Gross Rent Schedule',
		label: 'Define monthly gross rent values',
		action: 'update',
	};

	const advanceAmountModalData = {
		head: 'Advance Amount Schedule',
		label: 'Define advance amount and adjustment values',
		action: 'update',
	};

	return (
		<>
			<Row gutter={[16, 0]}>
				<Col xs={24}>
					<CardAccordion
						title="Monthly Rent Schedule"
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
							p: '10px',
							mb: 3,
						}}
						typographySX={{ fontSize: '13px !important' }}
						darkTitle={false}
						sx={{ mb: 2 }}
					>
						<GrossRent form={form} formListName="grossRentList" grossRentListSet={statusData?.grossRentListSet} screen={screen} />
					</CardAccordion>
				</Col>
				<Col xs={4}>
					<InputLabel label={constantFormValues?.paymentFrequency?.label} head={true} />
				</Col>
				<Col xs={8}>
					<Form.Item name={constantFormValues?.paymentFrequency?.key} rules={constantFormValues?.paymentFrequency?.rules}>
						<Select
							disabled={screen === 'VIEW'}
							showSearch
							style={{ width: '100%', height: '32px', borderRadius: '4px' }}
							filterOption={true}
							placeholder="Select from the list"
							optionFilterProp="children"
						>
							{constantFormValues?.paymentFrequency?.options?.map(({ key, value, label }) => (
								<Select.Option key={key} value={value}>
									{label}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>
				<Col xs={4}>
					<InputLabel label={constantFormValues?.paymentModality?.label} head={true} />
				</Col>
				<Col xs={8}>
					<Form.Item name={constantFormValues?.paymentModality?.key} rules={constantFormValues?.paymentModality?.rules}>
						<Select
							disabled={screen === 'VIEW'}
							showSearch
							style={{ width: '100%', height: '32px', borderRadius: '4px' }}
							filterOption={true}
							placeholder="Select from the list"
							optionFilterProp="children"
						>
							{constantFormValues?.paymentModality?.options?.map(({ key, value, label }) => (
								<Select.Option key={key} value={value}>
									{label}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</Col>

				<Col xs={24}>
					<CardAccordion
						title="Advance Payment & Adjustment"
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
							p: '10px',
							mb: 3,
						}}
						typographySX={{ fontSize: '13px !important' }}
						darkTitle={false}
						sx={{ mb: 2 }}
					>
						<AdvanceAmount
							form={form}
							formListName="advanceAmountList"
							screen={screen}
							advanceAmountListSet={statusData?.advanceAmountListSet}
						/>
					</CardAccordion>
				</Col>
				<Col xs={24}>
					<CardAccordion
						title="Other Payments"
						customHeaderSX={{
							backgroundColor: '#e1f0fb !important',
							p: '10px',
						}}
						typographySX={{ fontSize: '13px !important' }}
						darkTitle={false}
					>
						<Row gutter={[16, 0]}>
							<Col xs={4}>
								<InputLabel label={constantFormValues?.upfrontLeasePayment?.label} head={true} />
							</Col>
							<Col xs={8}>
								<Form.Item name={constantFormValues?.upfrontLeasePayment?.key} rules={constantFormValues?.upfrontLeasePayment?.rules}>
									<NumericFormat
										size="large"
										decimalScale={2}
										placeholder="0.00"
										fixedDecimalScale
										thousandSeparator=","
										isAllowed={withValueCap}
										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
										customInput={Input}
										disabled={screen === 'VIEW'}
									/>
								</Form.Item>
							</Col>

							<Col xs={4}>
								<InputLabel label={constantFormValues?.initialDirectCost?.label} head={true} />
							</Col>
							<Col xs={8}>
								<Form.Item name={constantFormValues?.initialDirectCost?.key} rules={constantFormValues?.initialDirectCost?.rules}>
									<NumericFormat
										size="large"
										decimalScale={2}
										placeholder="0.00"
										fixedDecimalScale
										thousandSeparator=","
										isAllowed={withValueCap}
										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
										customInput={Input}
										disabled={screen === 'VIEW'}
									/>
								</Form.Item>
							</Col>

							<Col xs={4}>
								<InputLabel label={constantFormValues?.securityDeposit?.label} head={true} />
							</Col>
							<Col xs={8}>
								<Form.Item name={constantFormValues?.securityDeposit?.key} rules={constantFormValues?.securityDeposit?.rules}>
									<NumericFormat
										size="large"
										decimalScale={2}
										placeholder="0.00"
										fixedDecimalScale
										thousandSeparator=","
										isAllowed={withValueCap}
										style={{ textAlign: 'right', height: '32px', borderRadius: '4px', fontSize: '13px' }}
										customInput={Input}
										disabled={screen === 'VIEW'}
									/>
								</Form.Item>
							</Col>
						</Row>
					</CardAccordion>
				</Col>

				{/* Hidden form items to store the arrays for API call */}
				<Form.Item name="advanceAmounts" noStyle />
				<Form.Item name="grossRents" noStyle />
				<Form.Item noStyle name={constantFormValues?.inputChangeMonth?.key} />
			</Row>

			{showGrossRentModal && screen !== 'VIEW' && (
				<TableContentModal
					form={form}
					showModal={showGrossRentModal}
					setShowModal={setShowGrossRentModal}
					formListName="grossRentSchedule"
					modalData={grossRentModalData}
					numberOfColumn={3}
					// onDataSave={() => setHasGrossRentData(true)}
				/>
			)}

			{showAdvanceAmountModal && screen !== 'VIEW' && (
				<TableContentModal
					form={form}
					showModal={showAdvanceAmountModal}
					setShowModal={setAdvanceAmountModal}
					formListName="advanceAmountSchedule"
					modalData={advanceAmountModalData}
					numberOfColumn={4}
					// onDataSave={() => setHasAdvanceAmountData(true)}
				/>
			)}
		</>
	);
};

export default FinancialTermsConditions;
